import React from "react"
import Link from "../LinkWrapper"
import { BodyStylesProps } from "../BodyStyles"
import { cva, cx } from "class-variance-authority"

export const pageTitleClasses = cva(
  "page-title font-bold leading-tight text-3xl [&:is(*_+_.page-title)]:mt-2",
  {
    variants: {
      contrast: {
        true: "text-white",
        false: "text-inherit",
      },
    },
    defaultVariants: {
      contrast: false,
    },
  }
)

export interface PageTitleProps {
  /**
   * Whether to use the contrast color scheme. Should be used for content on dark
   * backgrounds.
   */
  contrast?: BodyStylesProps["contrast"]
  /**
   * The level of the heading element to render. Defaults to H1.
   */
  level?: 1 | 2 | 3 | 4 | 5 | 6
  /**
   * The class name to apply to the component
   */
  className?: string
  /**
   * The content of the page title
   */
  children: React.ReactNode
}

/**
 * The heading element that renders a main title on the page
 */
export const PageTitle = ({
  contrast,
  level = 1,
  className,
  children,
}: PageTitleProps) => {
  const Component = `h${level}` as keyof JSX.IntrinsicElements

  return (
    <Component className={pageTitleClasses({ contrast, className })}>
      {children}
    </Component>
  )
}

export interface InlineTitleProps {
  /**
   * If the inline title should display with a border to its left
   */
  border?: boolean
  /**
   * The parent item of the inline title - used if linking back to a parent item, e.g. breadcrumbs
   * @deprecated Any future work should use the Breadcrumbs component
   */
  parent?: {
    url: string
    title: string
  }
  /**
   * The color scheme of the page
   */
  colorScheme?: BodyStylesProps["colorScheme"]
  /**
   * Whether to use the contrast color scheme. Should be used for links on dark
   * backgrounds.
   */
  contrast?: BodyStylesProps["contrast"]
  /**
   * The content to be rendered in the inline title, e.g. text
   */
  children: React.ReactNode
  /**
   * The alignment of the text in each callout
   */
  textAlign?: "left" | "center"
}

/**
 * Renders a compact title, often used as an eyebrow above a heading. Will be deprecated.
 * @deprecated Consider using the <Eyebrow /> or <Breadcrumb /> component instead
 */
export const InlineTitle = ({
  border = true,
  parent,
  colorScheme,
  contrast,
  children,
  textAlign,
}: InlineTitleProps) => {
  return (
    <div
      className={`${
        border
          ? `border-0 border-l-4 border-solid px-2 ${
              colorScheme === "dark" ? `border-orange` : `border-cyan`
            }`
          : ``
      } uppercase tracking-wider text-sm font-bold ${
        contrast === true ? `text-white` : `text-gray-700`
      }`}
      data-analytics-region="Inline Title"
    >
      <ul
        className={`flex flex-wrap -ml-2 text-xs uppercase tracking-wide font-bold ${
          textAlign === `center` ? `justify-center` : ``
        }`}
      >
        {parent && (
          <li className={`relative pl-2 pr-2`}>
            <Link
              to={parent.url}
              className={`text-action-link hover:text-action focus:text-action focus:outline-none`}
              data-analytics-title={parent.url}
            >
              {parent.title}
            </Link>
            <i
              className={`icon-chevron-right -legacy-translate-1/2 absolute left-full top-1/2 text-gray-300`}
            ></i>
          </li>
        )}
        <li
          className={`pl-2 ${
            contrast === true ? `text-white` : `text-gray-700`
          }`}
        >
          {children}
        </li>
      </ul>
    </div>
  )
}

export interface MutedHeadingProps {
  /**
   * The class name to apply to the component
   */
  className?: string
  /**
   * The content of the muted heading
   */
  children: React.ReactNode
}

/**
 * A muted version of a H3 Heading
 * @deprecated Use the Heading component instead
 */
export const MutedHeading = ({ className, children }: MutedHeadingProps) => {
  return (
    <h3 className={cx("text-xl text-gray-600 uppercase font-bold", className)}>
      {children}
    </h3>
  )
}
